import React, { useState } from 'react';
import { 
  QrCode, Download, Loader2, Link, Wifi, Mail, Phone, 
  MessageSquare, Copy, Check, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';

type QRType = 'url' | 'text' | 'wifi' | 'email' | 'phone' | 'sms';

interface WifiSettings {
  ssid: string;
  password: string;
  security: 'WPA' | 'WEP' | 'nopass';
  hidden: boolean;
}

const QRCodeGenerator: React.FC = () => {
  const [qrType, setQrType] = useState<QRType>('url');
  const [data, setData] = useState('');
  const [wifiSettings, setWifiSettings] = useState<WifiSettings>({
    ssid: '',
    password: '',
    security: 'WPA',
    hidden: false
  });
  const [size, setSize] = useState(300);
  const [fillColor, setFillColor] = useState('#000000');
  const [backColor, setBackColor] = useState('#FFFFFF');
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [style, setStyle] = useState('square');
  
  const [generatedQR, setGeneratedQR] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const qrTypes = [
    { id: 'url' as QRType, name: 'URL', icon: Link, placeholder: 'https://example.com' },
    { id: 'text' as QRType, name: 'Text', icon: MessageSquare, placeholder: 'Enter any text...' },
    { id: 'wifi' as QRType, name: 'WiFi', icon: Wifi, placeholder: '' },
    { id: 'email' as QRType, name: 'Email', icon: Mail, placeholder: 'email@example.com' },
    { id: 'phone' as QRType, name: 'Phone', icon: Phone, placeholder: '+1234567890' },
    { id: 'sms' as QRType, name: 'SMS', icon: MessageSquare, placeholder: '+1234567890' },
  ];

  const getFormattedData = (): string => {
    switch (qrType) {
      case 'url':
        return data.startsWith('http') ? data : `https://${data}`;
      case 'email':
        return `mailto:${data}`;
      case 'phone':
        return `tel:${data}`;
      case 'sms':
        return `sms:${data}`;
      default:
        return data;
    }
  };

  const generateQRCode = async () => {
    if (qrType === 'wifi') {
      if (!wifiSettings.ssid) {
        setError('Please enter a WiFi network name');
        return;
      }
    } else if (!data) {
      setError('Please enter data to encode');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      let response;

      if (qrType === 'wifi') {
        const formData = new FormData();
        formData.append('ssid', wifiSettings.ssid);
        formData.append('password', wifiSettings.password);
        formData.append('security', wifiSettings.security);
        formData.append('hidden', wifiSettings.hidden.toString());
        formData.append('size', size.toString());
        formData.append('fill_color', fillColor);
        formData.append('back_color', backColor);

        response = await axios.post(
          getApiUrl(API_CONFIG.ENDPOINTS.QRCODE_WIFI),
          formData,
          { responseType: 'blob' }
        );
      } else {
        const formData = new FormData();
        formData.append('data', getFormattedData());
        formData.append('size', size.toString());
        formData.append('error_correction', errorCorrection);
        formData.append('fill_color', fillColor);
        formData.append('back_color', backColor);
        formData.append('style', style);
        formData.append('output_format', 'png');

        response = await axios.post(
          getApiUrl(API_CONFIG.ENDPOINTS.QRCODE_GENERATE),
          formData,
          { responseType: 'blob' }
        );
      }

      const url = URL.createObjectURL(response.data);
      setGeneratedQR(url);
    } catch (err: any) {
      console.error('QR generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate QR code');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQR = () => {
    if (!generatedQR) return;
    
    const link = document.createElement('a');
    link.href = generatedQR;
    link.download = `qrcode_${qrType}_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async () => {
    if (!generatedQR) return;
    
    try {
      const response = await fetch(generatedQR);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const resetForm = () => {
    setData('');
    setWifiSettings({ ssid: '', password: '', security: 'WPA', hidden: false });
    setGeneratedQR(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">QR Code Generator</h2>
        <p className="text-gray-600">
          Create QR codes for URLs, WiFi, contacts, and more
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Input Section */}
        <div className="space-y-6">
          {/* QR Type Selection */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              QR Code Type
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {qrTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setQrType(type.id);
                      setData('');
                      setGeneratedQR(null);
                    }}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                      qrType === type.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-1" />
                    <span className="text-xs font-medium">{type.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Data Input */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {qrType === 'wifi' ? 'WiFi Settings' : 'Content'}
            </label>
            
            {qrType === 'wifi' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Network Name (SSID)</label>
                  <input
                    type="text"
                    value={wifiSettings.ssid}
                    onChange={(e) => setWifiSettings({ ...wifiSettings, ssid: e.target.value })}
                    placeholder="My WiFi Network"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Password</label>
                  <input
                    type="password"
                    value={wifiSettings.password}
                    onChange={(e) => setWifiSettings({ ...wifiSettings, password: e.target.value })}
                    placeholder="WiFi password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Security</label>
                    <select
                      value={wifiSettings.security}
                      onChange={(e) => setWifiSettings({ ...wifiSettings, security: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="WPA">WPA/WPA2</option>
                      <option value="WEP">WEP</option>
                      <option value="nopass">No Password</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={wifiSettings.hidden}
                        onChange={(e) => setWifiSettings({ ...wifiSettings, hidden: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-600">Hidden Network</span>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <textarea
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder={qrTypes.find(t => t.id === qrType)?.placeholder}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            )}
          </div>

          {/* Customization */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Customization
            </label>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Size: {size}px</label>
                <input
                  type="range"
                  min="100"
                  max="1000"
                  step="50"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Error Correction</label>
                <select
                  value={errorCorrection}
                  onChange={(e) => setErrorCorrection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">QR Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={fillColor}
                    onChange={(e) => setFillColor(e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Background</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={backColor}
                    onChange={(e) => setBackColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={backColor}
                    onChange={(e) => setBackColor(e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Style Selection */}
            <div className="mt-4">
              <label className="block text-xs text-gray-500 mb-2">Style</label>
              <div className="flex gap-2">
                {['square', 'rounded', 'circle'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStyle(s)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      style === s
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateQRCode}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-200"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <QrCode className="w-5 h-5" />
                Generate QR Code
              </>
            )}
          </button>
        </div>

        {/* Right: Preview Section */}
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-xl p-6 min-h-[400px] flex items-center justify-center">
            {generatedQR ? (
              <div className="text-center">
                <div className="bg-white p-4 rounded-lg inline-block shadow-xl">
                  <img
                    src={generatedQR}
                    alt="Generated QR Code"
                    className="max-w-full"
                    style={{ maxHeight: '350px' }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <QrCode className="w-24 h-24 mx-auto mb-4 opacity-30" />
                <p>Your QR code will appear here</p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          {generatedQR && (
            <div className="flex gap-3">
              <button
                onClick={downloadQR}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download PNG
              </button>
              
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white font-medium rounded-xl hover:bg-gray-700 transition-colors"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
              
              <button
                onClick={resetForm}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
